<template>
  <b-modal v-model="modal" title="Importar marcas via planilha" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true" hide-footer>
    <!-- Step 1: Instructions -->
    <div v-if="step === 1">
      <div class="text-center mb-4">
        <div class="import-icon-wrapper mb-3"><i class="bx bx-spreadsheet"></i></div>
        <h5 class="mb-1">Atribuição em massa da Trilha presencial</h5>
        <p class="text-muted mb-0">Defina as marcas de centenas de participantes de uma só vez usando uma planilha.</p>
      </div>

      <div class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <h6 class="mb-3"><i class="bx bx-info-circle text-primary mr-1"></i> Como funciona</h6>
          <div class="steps-list">
            <div class="step-item"><span class="step-number">1</span><div><strong>Baixe o template</strong><p class="text-muted mb-0 font-size-12">Já vem com as colunas certas.</p></div></div>
            <div class="step-item"><span class="step-number">2</span><div><strong>Preencha os dados</strong><p class="text-muted mb-0 font-size-12">Uma linha por participante. Casamos por e-mail (e, na falta, por nome).</p></div></div>
            <div class="step-item"><span class="step-number">3</span><div><strong>Analise antes de confirmar</strong><p class="text-muted mb-0 font-size-12">Mostramos o panorama (casados / não encontrados) e você confirma.</p></div></div>
          </div>
        </div>
      </div>

      <div class="card border shadow-none mb-3">
        <div class="card-body p-3">
          <h6 class="mb-3"><i class="bx bx-columns text-primary mr-1"></i> Colunas da planilha</h6>
          <div class="table-responsive">
            <table class="table table-sm table-bordered mb-0 font-size-13">
              <thead class="thead-light"><tr><th style="width: 25%">Coluna</th><th>Descrição</th></tr></thead>
              <tbody>
                <tr><td><code>email</code> <span class="badge badge-soft-danger font-size-10 ml-1">recomendado</span></td><td>E-mail do participante — chave principal de casamento.</td></tr>
                <tr><td><code>nome</code></td><td>Nome completo — usado no casamento quando não há e-mail.</td></tr>
                <tr><td><code>marcas</code></td><td>Marcas separadas por vírgula, pelo <strong>código</strong> ou <strong>nome</strong>. Ex.: <code>Marca 01, SAS</code>.</td></tr>
              </tbody>
            </table>
          </div>
          <div class="text-muted font-size-12 mt-2" v-if="marks && marks.length">
            Marcas disponíveis:
            <span v-for="(m, i) in marks" :key="m.id"><strong>{{ m.code || m.name }}</strong><span v-if="i < marks.length - 1">, </span></span>
          </div>
        </div>
      </div>

      <div class="custom-control custom-checkbox mb-3">
        <input type="checkbox" class="custom-control-input" id="replaceMarksImport" v-model="replace">
        <label class="custom-control-label" for="replaceMarksImport">Substituir as marcas atuais do participante (desmarcado = apenas adiciona)</label>
      </div>

      <div class="text-center mb-3">
        <button type="button" class="btn btn-outline-success" :disabled="downloading" @click="downloadTemplate()">
          <i class="bx bx-download mr-1"></i> {{ downloading ? 'Gerando...' : 'Baixar Template (.xlsx)' }}
        </button>
      </div>

      <hr class="mb-3 mt-1" />
      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="modal = false">Cancelar</b-button>
        <b-button variant="primary" @click="step = 2">Já tenho a planilha pronta <i class="bx bx-right-arrow-alt ml-1"></i></b-button>
      </div>
    </div>

    <!-- Step 2: Upload -->
    <div v-if="step === 2">
      <div class="text-center mb-4">
        <div class="import-icon-wrapper mb-3"><i class="bx bx-cloud-upload"></i></div>
        <h5 class="mb-1">Enviar planilha</h5>
        <p class="text-muted mb-0">Vamos <strong>analisar</strong> o arquivo antes de gravar qualquer coisa.</p>
      </div>

      <div
        class="drop-zone mb-3"
        :class="{ 'drop-zone-active': isDragging, 'drop-zone-has-file': file }"
        @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop" @click="$refs.fileInput.click()"
      >
        <input ref="fileInput" type="file" accept=".xlsx,.xls,.csv" class="d-none" @change="handleFileSelect" />
        <div v-if="!file" class="text-center">
          <i class="bx bx-cloud-upload drop-zone-icon text-muted"></i>
          <p class="mb-1">Arraste o arquivo aqui ou <strong class="text-primary">clique para selecionar</strong></p>
          <small class="text-muted">Formatos aceitos: .xlsx, .xls, .csv</small>
        </div>
        <div v-else class="text-center">
          <i class="bx bx-file drop-zone-icon text-success"></i>
          <p class="mb-1 font-weight-medium">{{ file.name }}</p>
          <small class="text-muted">{{ formatFileSize(file.size) }}</small><br />
          <a href="#" @click.stop.prevent="file = null" class="text-danger font-size-12 mt-1 d-inline-block"><i class="bx bx-x"></i> Remover arquivo</a>
        </div>
      </div>

      <hr class="mb-3 mt-1" />
      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="step = 1"><i class="bx bx-left-arrow-alt mr-1"></i> Voltar</b-button>
        <b-button variant="primary" :disabled="!file || busy" @click="analyze()">
          <i class="bx bx-search-alt mr-1"></i> {{ busy ? 'Analisando...' : 'Analisar planilha' }}
        </b-button>
      </div>
    </div>

    <!-- Step 3: Análise (prévia, sem gravar) -->
    <div v-if="step === 3">
      <div class="text-center mb-3">
        <div class="import-icon-wrapper mb-3" :class="{ 'import-icon-warning': analysis.errors > 0 }">
          <i :class="analysis.errors > 0 ? 'bx bx-error' : 'bx bx-check-circle'"></i>
        </div>
        <h5 class="mb-1">Análise da planilha</h5>
        <p class="text-muted mb-0">Confira o panorama abaixo. <strong>Nada foi salvo ainda.</strong></p>
      </div>

      <div class="row justify-content-center mt-3 mb-3">
        <div class="col-auto">
          <div class="result-card result-card-success">
            <span class="result-number">{{ analysis.imported }}</span>
            <span class="result-label">serão atribuídos</span>
          </div>
        </div>
        <div class="col-auto" v-if="analysis.errors > 0">
          <div class="result-card result-card-danger">
            <span class="result-number">{{ analysis.errors }}</span>
            <span class="result-label">não encontrados</span>
          </div>
        </div>
        <div class="col-auto" v-if="warnCount > 0">
          <div class="result-card result-card-warning">
            <span class="result-number">{{ warnCount }}</span>
            <span class="result-label">com aviso</span>
          </div>
        </div>
      </div>

      <div v-if="logEntries(analysis).length" class="mt-2">
        <h6 class="mb-2"><i class="bx bx-list-ul text-secondary mr-1"></i> Detalhes</h6>
        <div class="border rounded" style="max-height: 240px; overflow-y: auto; background: #f8f9fa;">
          <div v-for="(entry, i) in logEntries(analysis)" :key="i" class="d-flex align-items-start py-2 px-3"
               :class="{ 'border-bottom': i < logEntries(analysis).length - 1 }">
            <i class="bx mr-2 mt-1 font-size-14" :class="entry.status === 'error' ? 'bx-x-circle text-danger' : 'bx-error-circle text-warning'"></i>
            <div class="font-size-13">
              <span v-if="entry.row" class="badge badge-soft-secondary font-size-10 mr-1">linha {{ entry.row }}</span>
              <strong>{{ entry.name }}</strong>
              <span v-if="entry.message" class="d-block font-size-12 mt-1" :class="entry.status === 'error' ? 'text-danger' : 'text-warning'">{{ entry.message }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info font-size-13 mt-3 mb-3 d-flex align-items-start" role="alert">
        <i class="bx bx-bulb mr-2 font-size-16"></i>
        <div>
          Ao confirmar, as marcas serão {{ replace ? 'substituídas' : 'adicionadas' }} para os
          <strong>{{ analysis.imported }}</strong> participantes encontrados. Os não encontrados são ignorados.
        </div>
      </div>

      <hr class="mb-3 mt-1" />
      <div class="d-flex justify-content-between">
        <b-button variant="light" @click="step = 2"><i class="bx bx-left-arrow-alt mr-1"></i> Voltar</b-button>
        <b-button variant="primary" :disabled="busy || analysis.imported === 0" @click="confirmImport()">
          <i class="bx bx-check mr-1"></i> Confirmar e importar
        </b-button>
      </div>
    </div>

    <!-- Step 4: Processing + Result -->
    <div v-if="step === 4">
      <div v-if="processing" class="text-center py-4">
        <div class="import-icon-wrapper mb-3"><i class="bx bx-loader-alt bx-spin"></i></div>
        <h5 class="mb-2">Importando...</h5>
        <p class="text-muted mb-0">Aguarde enquanto as marcas são atribuídas.</p>
      </div>

      <div v-else>
        <div class="text-center">
          <div class="import-icon-wrapper import-icon-success mb-3"><i class="bx bx-check-circle"></i></div>
          <h5 class="mb-2">Importação concluída!</h5>
        </div>
        <div class="row justify-content-center mt-3 mb-3">
          <div class="col-auto">
            <div class="result-card result-card-success">
              <span class="result-number">{{ result.imported }}</span>
              <span class="result-label">atribuídos</span>
            </div>
          </div>
          <div class="col-auto" v-if="result.errors > 0">
            <div class="result-card result-card-danger">
              <span class="result-number">{{ result.errors }}</span>
              <span class="result-label">não encontrados</span>
            </div>
          </div>
        </div>
        <hr class="mb-3 mt-3" />
        <div class="text-center"><b-button variant="primary" @click="finish()">Fechar</b-button></div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { errorMessage } from '@/components/alertsMessages'
import services from './services'

export default {
  name: 'trilha-import-modal',
  props: {
    marks: { type: Array, default: () => [] }
  },
  data () {
    return {
      modal: false,
      step: 1,
      file: null,
      isDragging: false,
      replace: true,
      downloading: false,
      busy: false,
      processing: false,
      analysis: { imported: 0, errors: 0, log: [] },
      result: { imported: 0, errors: 0, log: [] }
    }
  },
  computed: {
    warnCount () {
      return (this.analysis.log || []).filter(e => e.status === 'warning').length
    }
  },
  methods: {
    open () {
      this.step = 1
      this.file = null
      this.isDragging = false
      this.busy = false
      this.processing = false
      this.analysis = { imported: 0, errors: 0, log: [] }
      this.result = { imported: 0, errors: 0, log: [] }
      this.modal = true
    },
    logEntries (r) {
      return (r.log || []).filter(e => e.status === 'error' || e.status === 'warning')
    },
    handleFileSelect (e) {
      const files = e.target.files
      if (files && files.length) this.file = files[0]
    },
    handleDrop (e) {
      this.isDragging = false
      const files = e.dataTransfer.files
      if (files && files.length) {
        const ext = files[0].name.split('.').pop().toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext)) this.file = files[0]
        else errorMessage('Formato não suportado. Use .xlsx, .xls ou .csv')
      }
    },
    formatFileSize (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
    async downloadTemplate () {
      this.downloading = true
      try {
        const res = await services.downloadTemplate()
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'template_trilha_presencial.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        errorMessage('Falha ao baixar o template.')
      } finally {
        this.downloading = false
      }
    },
    buildForm (dryRun) {
      const formData = new FormData()
      formData.append('file', this.file)
      formData.append('replace', this.replace ? 1 : 0)
      formData.append('dry_run', dryRun ? 1 : 0)
      return formData
    },
    async analyze () {
      if (!this.file) return
      this.busy = true
      try {
        const res = (await services.importFile(this.buildForm(true))).data
        this.analysis = { imported: res.imported || 0, errors: res.errors || 0, log: res.log || [] }
        this.step = 3
      } catch (e) {
        errorMessage((e.response && e.response.data && e.response.data.message) || 'Erro ao analisar a planilha.')
      } finally {
        this.busy = false
      }
    },
    async confirmImport () {
      this.busy = true
      this.processing = true
      this.step = 4
      try {
        const res = (await services.importFile(this.buildForm(false))).data
        this.result = { imported: res.imported || 0, errors: res.errors || 0, log: res.log || [] }
        this.processing = false
        this.$emit('imported')
      } catch (e) {
        this.processing = false
        errorMessage((e.response && e.response.data && e.response.data.message) || 'Erro no import.')
        this.step = 3
      } finally {
        this.busy = false
      }
    },
    finish () {
      this.modal = false
    }
  }
}
</script>

<style scoped>
.import-icon-wrapper { width: 64px; height: 64px; border-radius: 50%; background-color: rgba(85, 110, 230, 0.1); display: inline-flex; align-items: center; justify-content: center; }
.import-icon-wrapper i { font-size: 28px; color: #556ee6; }
.import-icon-success { background-color: rgba(52, 195, 143, 0.1) !important; }
.import-icon-success i { color: #34c38f !important; }
.import-icon-warning { background-color: rgba(241, 180, 76, 0.1) !important; }
.import-icon-warning i { color: #f1b44c !important; }

.steps-list { display: flex; flex-direction: column; gap: 12px; }
.step-item { display: flex; align-items: flex-start; gap: 12px; }
.step-number { width: 26px; height: 26px; min-width: 26px; border-radius: 50%; background-color: #556ee6; color: #fff; font-size: 13px; font-weight: 600; display: flex; align-items: center; justify-content: center; margin-top: 1px; }

.drop-zone { border: 2px dashed #ced4da; border-radius: 8px; padding: 30px 20px; cursor: pointer; transition: all 0.2s ease; background: #fafafa; }
.drop-zone:hover { border-color: #556ee6; background: rgba(85, 110, 230, 0.03); }
.drop-zone-active { border-color: #556ee6; background: rgba(85, 110, 230, 0.06); }
.drop-zone-has-file { border-color: #34c38f; border-style: solid; background: rgba(52, 195, 143, 0.03); }
.drop-zone-icon { font-size: 36px; display: block; margin-bottom: 8px; }

.result-card { text-align: center; padding: 16px 28px; border-radius: 8px; min-width: 120px; }
.result-card-success { background: rgba(52, 195, 143, 0.1); }
.result-card-danger { background: rgba(244, 106, 106, 0.1); }
.result-card-warning { background: rgba(241, 180, 76, 0.1); }
.result-number { display: block; font-size: 28px; font-weight: 700; line-height: 1.2; }
.result-card-success .result-number { color: #34c38f; }
.result-card-danger .result-number { color: #f46a6a; }
.result-card-warning .result-number { color: #f1b44c; }
.result-label { display: block; font-size: 12px; color: #74788d; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }

code { font-size: 12px; }
</style>
